export const brandVoice = {
  title: 'Brand Voices',
  subtitle: 'Define communication guidelines to maintain consistency in your content',

  actions: {
    new: 'New Voice',
    edit: 'Edit',
    delete: 'Delete',
    duplicate: 'Duplicate',
    viewDetails: 'View Details',
    save: 'Save',
    cancel: 'Cancel',
    back: 'Back to Voices',
  },

  table: {
    name: 'Name',
    voiceTone: 'Voice Tone',
    elements: 'Elements',
    actions: 'Actions',
    openMenu: 'Open menu',
    empty: {
      title: 'No brand voices configured',
      subtitle: 'Create your first brand voice to get started',
      cta: 'Create brand voice',
    },
  },

  form: {
    createTitle: 'New Brand Voice',
    editTitle: 'Edit Brand Voice',

    basicInfo: {
      title: 'Basic Information',
      subtitle: 'Main data for your brand voice',
    },

    name: {
      label: 'Voice Name',
      placeholder: 'e.g., My Main Brand',
      required: 'Name is required',
      hint: 'A descriptive name to identify this brand voice',
    },

    voiceTone: {
      label: 'Voice Tone',
      placeholder:
        'e.g., Professional, approachable, and trustworthy. Conveys security and expertise without being arrogant.',
      required: 'Voice tone is required',
      tooltip:
        'Describe how your brand should sound. E.g., Professional but friendly, technical but accessible.',
    },

    targetAudience: {
      label: 'Target Audience',
      placeholder: 'e.g., CTOs and technical leaders of B2B companies in Latin America',
      tooltip: 'Who you are primarily addressing with this brand voice.',
    },

    identity: {
      title: 'Brand Identity',
      subtitle: 'Values and style that define your communication',
    },

    brandValues: {
      label: 'Brand Values',
      placeholder: 'e.g., Innovation, transparency, excellence, commitment',
      hint: 'Separate values with commas',
    },

    communicationStyle: {
      label: 'Communication Style',
      placeholder: 'e.g., Direct, informative, with supporting data and practical examples',
    },

    guidelines: {
      title: 'Communication Guidelines',
      subtitle: 'Define what to do and what to avoid in communication',

      do: {
        label: 'What to Do',
        placeholder: 'e.g., Use inclusive language and press Enter',
        tooltip: 'Positive guidelines: what type of language and approach to use.',
      },

      dont: {
        label: "What NOT to Do",
        placeholder: 'e.g., Use excessive technical jargon and press Enter',
        tooltip: 'Restrictions: what to avoid in communication.',
      },
    },

    examples: {
      title: 'Communication Examples',
      subtitle: 'Include text fragments that exemplify how the brand should sound',
      placeholder:
        'Paste here real examples of communication that represent your brand voice well. They can be email fragments, posts, product descriptions, etc.',
    },

    preview: {
      title: 'Preview',
      subtitle: 'This is how your brand voice will look',
      audience: 'Audience',
      values: 'Values',
      guidelines: 'Guidelines',
      elementsCompleted: 'Elements completed',
      defaultName: 'Voice name',
      defaultDescription: 'Voice tone description...',
    },

    progress: {
      complete: '% complete',
    },

    saving: 'Saving...',
    creating: 'Creating...',
    updating: 'Updating...',
  },

  delete: {
    title: 'Delete Brand Voice',
    description: 'This action cannot be undone. The brand voice will be permanently deleted.',
    confirm: 'Delete',
    cancel: 'Cancel',
    deleting: 'Deleting...',
  },

  toast: {
    createSuccess: 'Brand voice created successfully',
    createError: 'Error creating brand voice: {{message}}',
    updateSuccess: 'Brand voice updated successfully',
    updateError: 'Error updating brand voice: {{message}}',
    deleteSuccess: 'Brand voice deleted successfully',
    deleteError: 'Error deleting brand voice: {{message}}',
    duplicateSuccess: 'Brand voice duplicated successfully',
    duplicateError: 'Error duplicating brand voice: {{message}}',
  },

  templates: {
    pageTitle: 'Choose a Template',
    pageSubtitle: 'Start with a proven voice archetype and customize it to your brand',
    startFromScratch: 'Start from Scratch',
    startFromScratchDescription: 'Build your brand voice from the ground up with a blank canvas',
    previewDos: 'Do',
    previewDonts: "Don't",

    professional: {
      name: 'Professional / Corporate',
      description: 'Authoritative and polished tone for B2B and enterprise communications',
    },
    friendly: {
      name: 'Friendly / Conversational',
      description: 'Warm and approachable like talking to a knowledgeable friend',
    },
    bold: {
      name: 'Bold / Disruptive',
      description: 'Confident and provocative tone that challenges the status quo',
    },
    educational: {
      name: 'Educational / Expert',
      description: 'Clear and methodical explanations that empower learning',
    },
    luxury: {
      name: 'Luxury / Premium',
      description: 'Elegant and refined language that evokes exclusivity',
    },
    playful: {
      name: 'Playful / Creative',
      description: 'Fun and imaginative voice full of personality and surprise',
    },
    empathetic: {
      name: 'Empathetic / Caring',
      description: 'Compassionate and supportive tone that makes people feel heard',
    },
    technical: {
      name: 'Technical / Developer',
      description: 'Precise and practical content for engineers and developers',
    },
    inspirational: {
      name: 'Inspirational / Motivational',
      description: 'Uplifting and energizing language that sparks possibility',
    },
    minimalist: {
      name: 'Minimalist / Direct',
      description: 'Every word counts — clear, concise, and purposeful communication',
    },
  },

  footer: {
    total: 'total',
  },
} as const
